export const ROUTING_NUMBER_LENGTH = 9;

/**
 * ABA check digit: 3(d1+d4+d7) + 7(d2+d5+d8) + 1(d3+d6+d9) must be a multiple of 10.
 * Catches most single-digit typos and transpositions without a network call.
 */
export function hasValidRoutingChecksum(value: string): boolean {
  if (!/^\d{9}$/.test(value)) return false;
  // All-zeros satisfies the checksum arithmetically but is not an assigned routing number.
  if (value === "000000000") return false;
  const d = value.split("").map(Number);
  const sum =
    3 * (d[0] + d[3] + d[6]) +
    7 * (d[1] + d[4] + d[7]) +
    1 * (d[2] + d[5] + d[8]);
  return sum % 10 === 0;
}

/** Digits only, capped at 9 — for onChange. */
export function normalizeRoutingNumber(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, ROUTING_NUMBER_LENGTH);
}

/**
 * Inline validity for a field the user is still typing in. `null` means "nothing to say yet" —
 * an incomplete number is not an error, so the message only appears once 9 digits are present.
 */
export function routingNumberError(value: string): string | null {
  if (value.length === 0) return null;
  if (value.length < ROUTING_NUMBER_LENGTH) return null;
  if (!hasValidRoutingChecksum(value)) {
    return "That routing number isn't valid. Please check it against your bank details.";
  }
  return null;
}

/** Blocking check for submit — an incomplete number IS an error here. */
export function routingNumberSubmitError(value: string): string | null {
  if (value.length !== ROUTING_NUMBER_LENGTH) {
    return "Routing number must be 9 digits.";
  }
  if (!hasValidRoutingChecksum(value)) {
    return "That routing number isn't valid. Please check it against your bank details.";
  }
  return null;
}
